import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Droplets, Layers, Sun, Recycle } from 'lucide-react';
import PageBanner from '../components/common/PageBanner';
import { Section, Head, Card, Lead, SideImage } from '../components/common/Primitives';
import { sustainabilityItems, statistics } from '../data/site';
import banner from '../assets/banners/sustainability.jpg';
import solarField from '../assets/tech/solar-field.jpg';
import waterRoots from '../assets/tech/water-roots.jpg';

const ICONS = [Droplets, Layers, Sun, Recycle];

const Sustainability = () => (
  <div>
    <PageBanner
      image={banner}
      kicker="Sustainability"
      title="Luxury and restraint, together"
      lead="Annpriya is not only about eating better. Every box is part of an argument that premium food and a lighter footprint are not opposites."
    />

    <Section tone="paper">
      <Head
        eyebrow="Commitments"
        title="Four places it shows up"
        lead="These are operational choices rather than aspirations. Each one is a property of how the greenhouse is built."
        center
      />
      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {sustainabilityItems.map((item, i) => {
          const Icon = ICONS[i] || Droplets;
          return (
            <Card key={item.title} className="p-7">
              <span className="w-12 h-12 rounded-xl grid place-items-center bg-forest/10 text-forest">
                <Icon className="w-6 h-6" />
              </span>
              <h3 className="mt-5 font-display font-semibold text-[1.15rem] text-ink">{item.title}</h3>
              <p className="mt-2.5 text-soil/70 text-[14.5px] leading-relaxed">{item.description}</p>
            </Card>
          );
        })}
      </div>
    </Section>

    <Section tone="ink" tight className="fieldgrid">
      <dl className="grid grid-cols-2 lg:grid-cols-4 gap-8">
        {statistics.map((s) => (
          <div key={s.label}>
            <dt className="font-display font-semibold text-[2.5rem] md:text-[3rem] leading-none text-lime tabular-nums">
              {s.value}
              <span className="text-[1.4rem] align-top ml-0.5 text-lime/70">{s.unit}</span>
            </dt>
            <dd className="mt-3 text-paper/55 text-[14px] leading-snug">{s.label}</dd>
          </div>
        ))}
      </dl>
    </Section>

    <Section tone="paper">
      <div className="grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <Head
            eyebrow="Water"
            title="The 90% comes from measurement"
            lead="Recirculating hydroponics only saves water if the system knows how much it dispensed. The inline flow sensor that verifies every dose for safety is the same instrument that makes the water claim auditable."
          />
          <Link
            to="/farm-tech/safety"
            className="inline-flex items-center gap-2 mt-8 text-forest hover:text-ink font-medium transition-colors"
          >
            How dosing is verified <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <img
          src={waterRoots}
          alt="A lettuce plug lifted from a recirculating raft, roots intact"
          className="rounded-xl w-full ring-1 ring-soil/10"
        />
      </div>
    </Section>

    <Section tone="soil">
      <div className="grid lg:grid-cols-2 gap-14 items-center">
        <SideImage
          tone="dark"
          src={solarField}
          alt="A solar array and wind turbines beside a seedling bench"
        />
        <div>
          <Head
            tone="dark"
            eyebrow="Energy"
            title="Solar integration is underway"
            lead="The control system was designed offline-first, which means it runs on local power and local compute. That same property is what makes moving the greenhouse onto renewable supply a practical step rather than a rebuild."
          />
        </div>
      </div>
    </Section>

    <Section tone="paper">
      <div className="max-w-3xl mx-auto text-center">
        <Head eyebrow="Our mission" title="Cultivating a movement" center />
        <Lead className="mt-7">
          Every box you receive is part of a larger argument: that food can be grown cleanly, at
          quality, without asking the planet to pay for it. We are not just growing food.
        </Lead>
        <Link
          to="/membership"
          className="inline-flex items-center gap-2 mt-10 rounded-full bg-forest text-paper px-7 py-3.5 font-semibold hover:bg-ink transition-colors"
        >
          Join the 500 <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </Section>
  </div>
);

export default Sustainability;
